import Cookies from 'js-cookie';
import get from 'lodash/get.js';

import axios from '@/plugins/axios.js';
import { i18n } from '@/plugins/i18n.js';
import moment from '@/plugins/vue-moment.js';
import store from '@/store/Index.js';
import ErrorPackage from '@/utilities/ErrorPackage.js';

// Safe destructuring of tc function from i18n with fallback for test environment
const _tc =
    i18n.global?.tc ||
    ((key, count) => {
        // Simple fallback for pluralization in test environment
        const pluralMap = {
            second: count === 1 ? 'second' : 'seconds',
            minute: count === 1 ? 'minute' : 'minutes',
            hour: count === 1 ? 'hour' : 'hours',
            day: count === 1 ? 'day' : 'days',
            week: count === 1 ? 'week' : 'weeks',
            month: count === 1 ? 'month' : 'months',
            year: count === 1 ? 'year' : 'years',
        };
        return pluralMap[key] || key;
    });

export function spacify(str) {
    const result = str.replace(/([A-Z])/g, ' $1');
    return result.charAt(0).toUpperCase() + result.slice(1);
}

export function oxfordList(items, separator = 'and') {
    if (items.length === 0) {
        return '';
    }

    if (items.length === 1) {
        return items[0];
    }

    if (items.length === 2) {
        return `${items[0]} ${separator} ${items[1]}`;
    }

    return (
        items.slice(0, -2).join(', ') +
        (items.slice(0, -2).length ? ', ' : '') +
        items.slice(-2).join(`, ${separator} `)
    );
}

const delay = (function createDelay() {
    const timers = {};

    return function delayFunction({ callback, ms, uuid }) {
        clearTimeout(timers[uuid]);
        timers[uuid] = setTimeout(callback, ms);
    };
})();

export function getBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
    });
}

/**
 *
 * @param {Date} date
 * @param {string} timezone
 * @returns {*}
 */
export function localDateToUTC(date, timezone) {
    const convertDate = date instanceof Date ? date : new Date(date);

    return moment(convertDate).tz(timezone).utc();
}

export function UTCToLocalDate(date, timezone) {
    if (!(date instanceof Date)) {
        throw new Error('Parameter must be a native Date object.');
    }

    return new Date(moment.utc(date).toDate(), { timeZone: timezone });
}

export function daysBetweenDates(start, end, timezone) {
    const dates = [];

    const currentDate = timezone ? moment.utc(start).tz(timezone).startOf('day') : moment(start).startOf('day');

    const lastDate = timezone ? moment.utc(end).tz(timezone).endOf('day') : moment(start).endOf('day');

    do {
        dates.push(currentDate.clone());
    } while (currentDate.add(1, 'day').diff(lastDate) < 0);

    return dates;
}

/**
 * Download a file from the server
 * @param {string} url
 * @param {string|null} [name]
 * @param {object} [config]
 * @param {boolean} [open]
 * @returns {Promise<void>}
 */
export async function downloadFile({ url, name = null, config = {}, open = false }) {
    const response = await axios.get(url, { responseType: 'blob', ...config });

    const pseudoUrl = window.URL.createObjectURL(new Blob([response.data]));

    if (open) {
        const openUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));

        window.open(openUrl);
    }

    const link = document.createElement('a');

    const contentDisposition = response.headers['content-disposition'];

    if (!contentDisposition) {
        throw new Error('The response did not generate a file for download.');
    }

    const fileName = name || get(contentDisposition.match(/^attachment; filename="(.+)".*$/), 1, 'file.txt');

    link.href = pseudoUrl;

    link.setAttribute('download', fileName);

    document.body.appendChild(link);

    link.click();
    link.remove();
}

export function initialState(key, defaultValue) {
    // eslint-disable-next-line no-underscore-dangle
    return get(window.__INITIAL_STATE__, key, defaultValue);
}

export function handleResponseError(error) {
    const errorPackage = new ErrorPackage(initialState('Debug'));

    errorPackage.capture(error);

    // For 422 validation errors, don't show a generic snackbar.
    // The AutoSave system will display specific field-level validation errors
    // which are more helpful to users than "Please correct the fields with invalid data."
    if (error?.response?.status === 422) {
        errorPackage.disableSnackbar();
        errorPackage.userMessage = null;
        store.commit('PUSH_GLOBAL_ERROR', errorPackage);
        return;
    }

    // Generate a user-friendly message for display in snackbars
    const userMessage = errorPackage.getUserFriendlyMessage();

    // If getUserFriendlyMessage returns null (e.g., for access denied errors),
    // don't show a snackbar but still capture the error for debugging
    if (userMessage === null) {
        errorPackage.disableSnackbar();
        errorPackage.userMessage = null;
    } else {
        errorPackage.userMessage = userMessage;
    }

    store.commit('PUSH_GLOBAL_ERROR', errorPackage);
}

/**
 * Get a setting's value from any of the setting groups in the following priority: User, Institution, Environment.
 * @param key
 * @param defaultValue
 * @returns {unknown}
 */
export function setting(key, defaultValue) {
    let value = initialState(`UserSettings.${key}`, null);

    if (!value) value = initialState(`InstitutionSettings.${key}`, null);
    if (!value) value = initialState(`EnvironmentSettings.${key}`, null);

    return value ?? defaultValue;
}

export const MONTH = 30 * 24 * 60 * 60;
export const WEEK = 7 * 24 * 60 * 60;
export const DAY = 24 * 60 * 60;
export const HOUR = 60 * 60;
export const MINUTE = 60;

function getTimeUnit(seconds) {
    const timeUnits = [
        { divisor: MONTH, unit: 'month' },
        { divisor: WEEK, unit: 'week' },
        { divisor: DAY, unit: 'day' },
        { divisor: HOUR, unit: 'hour' },
        { divisor: MINUTE, unit: 'minute' },
    ];

    if (seconds === 0) {
        return { divisor: 1, unit: 'second' };
    }

    const matchingUnit = timeUnits.find(({ divisor }) => seconds % divisor === 0);
    return matchingUnit || { divisor: 1, unit: 'second' };
}

export function displaySeconds(seconds) {
    if (!seconds) {
        return 'N/A';
    }

    const { divisor, unit } = getTimeUnit(seconds);
    const units = seconds / divisor;
    // Pass the numeric count to trigger pluralization (e.g., "minute|minutes")
    return `${units} ${i18n.global.t(unit, units)}`;
}

function formatDurationPart(duration, unit, abbrev) {
    let value;

    if (unit === 'year') {
        value = Math.floor(duration.years());
    } else if (unit === 'month') {
        value = Math.floor(duration.months());
    } else if (unit === 'day') {
        value = Math.floor(duration.days());
    } else if (unit === 'hour') {
        value = Math.floor(duration.hours());
    } else if (unit === 'minute') {
        value = Math.floor(duration.minutes());
    } else if (unit === 'second') {
        value = Math.floor(duration.seconds());
    } else {
        return null;
    }

    if (value < 1) return null;

    if (abbrev) {
        const abbrevUnit = unit.charAt(0);
        return `${value}${abbrevUnit}`;
    }

    const pluralUnit = value > 1 ? `${unit}s` : unit;
    return `${value} ${pluralUnit}`;
}

/**
 * @todo Support translations
 * @param period
 * @returns {string}
 */
export function formatDuration(period, abbrev = false) {
    const duration = moment.duration(period);

    // return nothing when the duration is falsy or not correctly parsed (P0D)
    if (!duration || duration.toISOString() === 'P0D') return '';

    const parts = [];
    const units = ['year', 'month', 'day', 'hour', 'minute', 'second'];

    units.forEach(unit => {
        const part = formatDurationPart(duration, unit, abbrev);
        if (part) parts.push(part);
    });

    return parts.join(', ');
}

export { delay };

export function field(name) {
    return {
        get() {
            return this.getField(name);
        },

        set(value) {
            this.setField(name, value);
        },
    };
}

export function relativeTimestamp(
    timestamp,
    { pastLabel = 'Occurred on', futureLabel = 'Scheduled for', longFormat = true } = {}
) {
    const isFuture = moment().isBefore(timestamp);
    const relevance = isFuture ? futureLabel || 'Occurred on' : pastLabel || 'Scheduled for';

    const fullFormat = longFormat ? 'LLL zz' : 'ddd M/D [at] h:mm A';

    return timestamp?.calendar(null, {
        sameDay: '[Today at] h:mm A',
        nextDay: '[Tomorrow at] h:mm A',
        nextWeek: 'dddd [at] h:mm A',
        lastDay: '[Yesterday at] h:mm A',
        lastWeek: 'dddd [at] h:mm A',
        sameElse: `[${relevance}] ${fullFormat}`,
    });
}

export function ellipsify(text, maxWords = 100) {
    const words = text.trim().split(/\s+/);

    if (words.length <= maxWords) {
        return text;
    }
    return `${words.slice(0, maxWords).join(' ')}...`;
}

export async function asyncForEach(array, callback) {
    return Promise.all(array.map(callback));
}

export function modelToString(model) {
    return spacify(model.replace('App\\Models\\', ''));
}

/**
 * Removes HTML tags from a given string.
 *
 * @param {string} str - The string to sanitize.
 * @returns {string} - The string without HTML tags.
 */
export function removeHTMLTags(str) {
    return typeof str === 'string' ? str.replace(/<\/?[^>]+(>|$)/g, '') : '';
}

export function rootDomain(hostname) {
    const parts = hostname.split('.');

    if (parts.length > 2) {
        const tlds = ['co.uk', 'gov.uk', 'ac.uk', 'com.au', 'org.au'];
        const lastTwoParts = parts.slice(-2).join('.');

        if (tlds.includes(lastTwoParts)) {
            return parts.slice(-3).join('.');
        }
    }

    return parts.slice(-2).join('.');
}

/**
 * Get signed cookies from the server in order to retrieve CDN content.
 *
 * @param service
 * @returns {Promise<void|any>}
 */
export async function getSignedCookies(service) {
    const requiredCookies = ['CloudFront-Key-Pair-Id', 'CloudFront-Policy', 'CloudFront-Signature'];

    if (requiredCookies.every(cookie => Cookies.get(cookie))) {
        return null;
    }

    const { data } = await axios.get(`/api/signed-cookies?service=${service}`);

    Object.entries(data.data).forEach(([key, value]) => {
        Cookies.set(key, value, {
            domain: rootDomain(initialState('AppHost')),
        });
    });

    return data;
}

/**
 * Generate a two‐letter avatar monogram from a given firstName and lastName.
 * Handles multi‐word names, skips common “particles,” and falls back to single‐token logic.
 *
 * @param {string} firstName  – The user’s given name(s), e.g. "María" or "Juan Martín"
 * @param {string} lastName   – The user’s family name(s), e.g. "García López" or "van Beethoven"
 * @returns {string} Two uppercase initials, or an empty string if no valid letters are found.
 */
/* eslint-disable complexity */
export function initials(firstName, lastName) {
    // If both inputs are missing or empty, return empty string.
    if (!firstName && !lastName) {
        return '';
    }

    // 1. Combine firstName + lastName into one raw string, trim whitespace.
    const raw = `${firstName || ''} ${lastName || ''}`.trim();
    if (raw === '') {
        return '';
    }

    // 2. Split into tokens on whitespace, hyphens, or apostrophes.
    //    e.g. "Anne‐Marie O'Connor" → ["Anne", "Marie", "O", "Connor"]
    let tokens = raw.split(/\s+|[-–—]+|['’]+/);

    // 3. Strip out any non‐letter characters (using Unicode letter property).
    //    If a token becomes empty after stripping, drop it.
    tokens = tokens.map(token => token.replace(/[^\p{L}]/gu, '')).filter(token => token.length > 0);

    if (tokens.length === 0) {
        return '';
    }

    // 4. If there's exactly one token, use its first two letters (or duplicate if length is 1).
    if (tokens.length === 1) {
        const single = tokens[0];
        if (single.length >= 2) {
            return (single[0] + single[1]).toUpperCase();
        }
        // e.g. name "Q" → "QQ"
        return (single[0] + single[0]).toUpperCase();
    }

    // 5. If there are 2 or more tokens, pick:
    //    - firstInitial: first character of tokens[0]
    //    - lastInitial: first character of tokens[lastNonParticleIndex]
    //
    //    We skip over any “particles” (e.g. "de", "da", "del", "van", "von", etc.)
    //    when choosing the “last” token.

    // 5a. Define a small list of common particles/connectors to skip.
    const particles = ['da', 'de', 'del', 'di', 'do', 'dos', 'du', 'la', 'le', 'van', 'von', 'bin', 'al', 'al-', 'abu'];

    // 5b. Find the last token index that is not in the particles list.
    let lastIdx = tokens.length - 1;
    while (lastIdx > 0 && particles.includes(tokens[lastIdx].toLowerCase())) {
        lastIdx--;
    }

    // 6. Grab first letter of the first token, and first letter of the chosen last token.
    const firstInitial = tokens[0][0] || '';
    const lastInitial = tokens[lastIdx][0] || '';

    // If for some reason either initial is missing, fall back to single‐token logic:
    if (!firstInitial && !lastInitial) {
        return '';
    }
    if (firstInitial && !lastInitial) {
        const fallback = tokens[0];
        return fallback.length >= 2
            ? (fallback[0] + fallback[1]).toUpperCase()
            : (fallback[0] + fallback[0]).toUpperCase();
    }
    if (!firstInitial && lastInitial) {
        const fallback = tokens[lastIdx];
        return fallback.length >= 2
            ? (fallback[0] + fallback[1]).toUpperCase()
            : (fallback[0] + fallback[0]).toUpperCase();
    }

    // 7. Return the two initials in uppercase.
    return (firstInitial + lastInitial).toUpperCase();
}
/* eslint-enable complexity */

export function errorMessageForStatus(status) {
    switch (status) {
        case 400:
            return 'An error occurred while processing your request.';
        case 401:
            return 'Please log in to continue.';
        case 403:
            return 'You are not authorized to access this resource.';
        case 404:
            return 'The resource you are looking for does not exist.';
        case 405:
            return 'The method you are trying to use is not allowed.';
        case 409:
            return 'The resource you are trying to create already exists.';
        case 422:
            return 'A validation error occurred while processing your request.';
        default:
            return 'An error occurred while processing your request.';
    }
}

/**
 * File Type Validation Utilities
 */

/**
 * Validates if a file matches any of the allowed MIME types or extensions
 * @param {File} file - The file to validate
 * @param {Array} allowedMimeTypes - Array of allowed MIME types
 * @param {Array} allowedExtensions - Array of allowed file extensions (with dots, e.g. ['.pdf', '.png'])
 * @returns {boolean} - True if file is valid, false otherwise
 */
export function validateFileType(file, allowedMimeTypes = [], allowedExtensions = []) {
    if (!file) return false;

    // Check MIME type first (most reliable)
    if (allowedMimeTypes.length > 0 && allowedMimeTypes.includes(file.type)) {
        return true;
    }

    // Fallback to extension check for files with generic or missing MIME types
    if (allowedExtensions.length > 0) {
        const fileName = file.name.toLowerCase();
        return allowedExtensions.some(ext => fileName.endsWith(ext.toLowerCase()));
    }

    // If no allowed types specified, consider it valid
    return allowedMimeTypes.length === 0 && allowedExtensions.length === 0;
}

/**
 * Gets a user-friendly file extension from a file
 * @param {File} file - The file to get extension from
 * @returns {string} - The file extension with dot (e.g. '.pdf')
 */
export function getFileExtension(file) {
    if (!file || !file.name) return '';
    const parts = file.name.split('.');
    return parts.length > 1 ? `.${parts.pop().toLowerCase()}` : '';
}

/**
 * Generates a user-friendly error message for invalid file types
 * @param {File} file - The invalid file
 * @param {Array} allowedExtensions - Array of allowed file extensions
 * @param {string} customMessage - Optional custom message
 * @returns {string} - Error message
 */
export function generateFileTypeErrorMessage(file, allowedExtensions = [], customMessage = null) {
    const fileExtension = getFileExtension(file);
    const baseMessage = `The file "${file.name}" (${fileExtension}) is not a supported file type.`;

    if (customMessage) {
        return `${baseMessage} ${customMessage}`;
    }

    if (allowedExtensions.length > 0) {
        return `${baseMessage} Please upload a file with one of these extensions: ${allowedExtensions.join(', ')}.`;
    }

    return baseMessage;
}

/**
 * Predefined file type configurations for common upload scenarios
 */
export const FILE_TYPE_CONFIGS = {
    slideDeck: {
        mimeTypes: [
            'application/vnd.apple.keynote',
            'application/vnd.oasis.opendocument.presentation',
            'application/mspowerpoint',
            'application/vnd.openxmlformats-officedocument.presentationml.template',
            'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
            'application/vnd.ms-powerpoint',
            'application/vnd.ms-powerpoint.presentation.macroenabled.12',
            'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            'application/vnd.stardivision.draw',
        ],
        extensions: ['.pptx', '.ppt', '.odp', '.potx', '.ppsx', '.pptm', '.pot', '.pps', '.key', '.sda'],
        message: 'Please upload a slide deck file.',
    },
    video: {
        mimeTypes: ['video/mp4', 'video/quicktime'],
        extensions: ['.mp4', '.mov'],
        message: 'Please upload a video file (MP4 or MOV).',
    },
    image: {
        mimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        extensions: ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
        message: 'Please upload an image file.',
    },
    document: {
        mimeTypes: [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'text/plain',
        ],
        extensions: ['.pdf', '.doc', '.docx', '.txt'],
        message: 'Please upload a document file.',
    },
};

/**
 * Validates multiple files against a file type configuration
 * @param {FileList|Array} files - Files to validate
 * @param {Object} config - File type configuration (from FILE_TYPE_CONFIGS or custom)
 * @returns {Object} - Validation result with { valid: File[], invalid: File[], errors: string[] }
 */
export function validateFiles(files, config) {
    const fileArray = Array.from(files);
    const valid = [];
    const invalid = [];
    const errors = [];

    fileArray.forEach(file => {
        if (validateFileType(file, config.mimeTypes, config.extensions)) {
            valid.push(file);
        } else {
            invalid.push(file);
            errors.push(generateFileTypeErrorMessage(file, config.extensions, config.message));
        }
    });

    return { valid, invalid, errors };
}
